// One WebGL renderer per conversation. Inactive messages retain only an image
// and camera settings; their geometry, materials, and textures are released.
export class ViewerSession {
    constructor() {
        this.clients = new Map()
        this.scene = null
        this.currentId = null
        this.targetId = null
        this.preferredId = null
        this.lockedId = null
        this.version = 0
        this.processedVersion = 0
        this.running = null
        this.disposed = false
    }

    register(client) {
        if (this.disposed) return
        this.clients.set(client.id, { ...client, data: null, pending: false, view: null, renderedData: null })
        if (client.id === this.preferredId) this.activate(client.id)
    }

    prefer(id, locked = false) {
        this.preferredId = id
        this.lockedId = locked ? id : null
        if (this.clients.has(id)) this.activate(id)
    }

    update(id, data, pending) {
        const client = this.clients.get(id)
        if (!client || this.disposed) return
        const changed = client.data !== data
        client.data = data
        client.pending = pending
        if (this.currentId === id) this.scene?.setRotating(pending)
        if (changed && this.targetId === id) this.request(id)
    }

    activate(id) {
        if (this.disposed || !this.clients.has(id) || (this.lockedId !== null && this.lockedId !== id)) return
        if (this.targetId === id && (this.running || this.currentId === id)) return
        this.request(id)
    }

    reload(id) {
        if (this.lockedId !== null && this.lockedId !== id) return
        if (!this.disposed && this.clients.has(id)) this.request(id)
    }

    request(id) {
        this.targetId = id
        this.version++
        this.scene?.cancelLoad()
        if (!this.running) {
            // Defer so registration, data updates, and rapid pointer events coalesce.
            this.running = Promise.resolve().then(() => this.run()).finally(() => { this.running = null })
        }
    }

    async run() {
        while (!this.disposed && this.processedVersion !== this.version) {
            const version = this.version
            const client = this.clients.get(this.targetId)
            this.processedVersion = version
            if (!client) continue
            try {
                await this.render(client, version)
            }
            catch (error) {
                if (!this.disposed && this.version === version && this.currentId === client.id && this.clients.has(client.id)) {
                    client.state({ loading: false, refreshing: false, loadError: 'The model preview could not be displayed.' })
                    client.error(error)
                }
            }
        }
    }

    async render(client, version) {
        const switching = this.currentId !== client.id
        if (this.currentId !== client.id) {
            const previous = this.clients.get(this.currentId)
            if (previous && this.scene) {
                previous.view = this.scene.getView()
                // Render immediately before capture; preserveDrawingBuffer is unnecessary.
                // Wait for the image to decode and Vue to paint it before moving the canvas.
                try { await previous.snapshot(this.scene.capture(), previous.renderedData) }
                catch { /* Retain the previous snapshot or asset thumbnail if capture fails. */ }
                if (this.disposed) return
            }
            if (this.version !== version || !this.clients.has(client.id)) return
            if (previous && this.clients.get(previous.id) === previous) previous.state({ live: false, loading: false, refreshing: false })
            this.scene?.clear()
            this.currentId = client.id
            client.state({ live: true, canvasReady: false, refreshing: false })
            if (!this.scene) {
                this.scene = client.createScene({
                    onError: error => this.handleError(error),
                    onRestore: () => this.reload(this.targetId),
                    onBeforeModelChange: () => this.prepareRefresh()
                })
            }
            else this.scene.attach(client.container)
        }

        if (this.scene?.contextLost) {
            this.scene.dispose()
            this.scene = null
        }
        if (!this.scene) {
            client.state({ live: true, canvasReady: false, refreshing: false })
            this.scene = client.createScene({
                onError: error => this.handleError(error),
                onRestore: () => this.reload(this.targetId),
                onBeforeModelChange: () => this.prepareRefresh()
            })
        }

        this.scene.setRotating(client.pending)
        const data = client.data
        client.state({ loading: !!data, loadError: '' })
        if (!data) {
            this.scene.clear()
            client.renderedData = null
            client.state({ hasModel: false, loadedData: null, loading: false, canvasReady: true, refreshing: false })
            return
        }

        const loaded = await this.scene.load(data)
        if (this.disposed || this.version !== version || !this.clients.has(client.id) || !loaded) return
        if (switching && client.view) this.scene.restoreView(client.view)
        client.renderedData = data
        client.state({ hasModel: true, loadedData: data, loading: false, canvasReady: true, refreshing: false })
    }

    async prepareRefresh() {
        const client = this.clients.get(this.currentId)
        const version = this.version
        if (!client || this.targetId !== client.id || !client.renderedData) return
        try {
            const image = this.scene.capture()
            if (!image) return
            await client.snapshot(image)
            if (this.disposed || this.version !== version || this.clients.get(client.id) !== client) return
            // Paint the old frame over the canvas before the new model is committed.
            // Removing this cover after load lets CSS reveal the new frame in 300ms.
            await client.state({ refreshing: true })
        }
        catch { /* A failed snapshot must not prevent a valid model update. */ }
    }

    handleError(error) {
        const client = this.clients.get(this.currentId)
        if (!client || this.disposed) return
        client.state({ loading: false, refreshing: false, loadError: 'The model preview could not be displayed.' })
        client.error(error)
    }

    unregister(id) {
        this.clients.delete(id)
        if (this.currentId === id) {
            this.scene?.clear()
            this.scene?.stop()
            this.scene?.renderer.domElement.remove()
            this.currentId = null
        }
        if (this.targetId === id) {
            this.targetId = null
            this.version++
            this.scene?.cancelLoad()
        }
    }

    dispose() {
        this.disposed = true
        this.version++
        this.scene?.dispose()
        this.scene = null
        this.clients.clear()
    }
}
